import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, MessageCircle, LogIn, User as UserIcon, Phone, Mail, Paperclip, Loader2, Image as ImageIcon } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import { CONSUMER_GROUPS, GROUP_TO_CANONICAL, initialGroupKey } from '@/lib/inquiryCategory';

// Phase 12-1/12-2 · Shared consumer inquiry composer.
// Phase 13-C · Login-gated with auto-fill from LIVEMOA profile.
// STEP 5-CS-B · 이미지 첨부(최대 5장) 지원. 기존 /api/uploads · /api/files 100% REUSE.
//   • Not logged in → show Kakao login gate that preserves return_url.
//   • Logged in     → hide name/phone inputs, show read-only member card.
//   • Backend routes accept the session cookie and skip phone-verify when the
//     LIVEMOA user matches the order/seller/product context.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTACHMENTS = 5;
const IMG_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp';
const MAX_FILE_BYTES = 5 * 1024 * 1024;

const CATEGORY_LABEL = {
  product: '상품문의', order: '주문문의', payment: '결제문의', shipping: '배송문의',
  exchange: '교환문의', return: '반품문의', cancel: '취소문의', other: '기타문의',
};

// scope: 'order' | 'product' | 'seller'
//   order:   { order_id, verify_phone? }   (member: phone from profile / order match)
//   product: { product_id, name }
//   seller:  { seller_id, order_id?, product_id? }   (session identifies buyer)
export default function ConsumerInquiryModal({ open, onClose, scope, context, defaultCategory = 'other', onCreated }) {
  const [me, setMe] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  // STEP 5-CT-B · 소비자 UI 5종 그룹 (product/order/shipping/rma/other)
  // 저장 시 GROUP_TO_CANONICAL 로 기존 canonical 8종 중 대표값 1개로 매핑
  const [groupKey, setGroupKey] = useState(initialGroupKey(defaultCategory));
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [visibility, setVisibility] = useState('private');
  // Guest-only fallback field, kept only for legacy read-only display. Members
  // do NOT retype name/phone — those come from /auth/me.
  const [guestPhone, setGuestPhone] = useState('');
  // STEP 5-CS-B · 첨부 이미지 (최대 5장). 기존 POST /api/uploads (kind=generic) 재사용.
  const [attachments, setAttachments] = useState([]);   // [{file_id, url, mime, size}]
  const [uploadingIdx, setUploadingIdx] = useState(-1); // -1: none, >=0: 업로드 중인 슬롯 인덱스
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setAuthChecked(false);
    setAttachments([]); // 모달 열 때 초기화
    api.get('/auth/me')
      .then((r) => setMe(r.data))
      .catch(() => setMe(null))
      .finally(() => setAuthChecked(true));
  }, [open]);

  if (!open) return null;

  const startKakao = () => {
    const back = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/kakao/start?return_url=${back}`;
  };

  const uploadFiles = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    const remaining = MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) { toast.error(`이미지는 최대 ${MAX_ATTACHMENTS}장까지 첨부할 수 있습니다.`); return; }
    const picked = list.slice(0, remaining);
    if (list.length > remaining) toast.info(`이미지는 최대 ${MAX_ATTACHMENTS}장까지 · ${remaining}장만 추가되었습니다.`);
    for (const f of picked) {
      if (f.size > MAX_FILE_BYTES) { toast.error(`"${f.name}" 파일 크기가 5MB를 초과합니다.`); continue; }
      if (!IMG_ACCEPT.split(',').includes(f.type)) { toast.error(`"${f.name}" 지원하지 않는 형식입니다.`); continue; }
      setUploadingIdx(attachments.length);
      try {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('kind', 'generic');
        const r = await api.post('/uploads', fd);
        setAttachments((prev) => [...prev, {
          file_id: r.data.file_id,
          url: r.data.url,
          mime: f.type,
          size: f.size,
        }]);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `"${f.name}" 업로드 실패`);
      } finally { setUploadingIdx(-1); }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = async (idx) => {
    const target = attachments[idx];
    // 서버에도 삭제 요청 (best-effort · 실패해도 UI에서는 제거)
    if (target?.file_id) {
      try { await api.delete(`/uploads/${target.file_id}`); } catch { /* ignore */ }
    }
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!title.trim() || !content.trim()) { toast.error('제목과 내용은 필수입니다.'); return; }
    if (uploadingIdx >= 0) { toast.error('이미지 업로드가 완료될 때까지 기다려주세요.'); return; }
    setBusy(true);
    try {
      let url;
      const body = {
        // STEP 5-CT-B · 5-group key → canonical 8종 대표값으로 저장
        category: GROUP_TO_CANONICAL[groupKey] || 'other',
        title: title.trim(), content: content.trim(),
        // STEP 5-CS-B · attachments passthrough (기존 _create_inquiry payload 통과)
        attachments: attachments.map((a) => ({ url: a.url, file_id: a.file_id, mime: a.mime, size: a.size })),
      };
      if (scope === 'order') {
        url = `${API}/public/orders/${context.order_id}/inquiries`;
        // Member: server matches buyer_user_id against the order — phone check
        // is auto-skipped. Guest fallback still sends verify_phone.
        if (!me) body.verify_phone = context?.verify_phone || guestPhone;
      } else if (scope === 'product') {
        url = `${API}/public/products/${context.product_id}/inquiries`;
        body.visibility = visibility;
        // Backend auto-derives name/phone from the profile when logged in.
        if (!me) { body.name = '고객'; body.phone = guestPhone; }
      } else {
        url = `${API}/public/sellers/${context.seller_id}/inquiries`;
        body.order_id = context?.order_id;
        body.product_id = context?.product_id;
        if (!me) { body.name = '고객'; body.phone = guestPhone; }
      }
      const r = await axios.post(url, body, { withCredentials: true });
      toast.success('문의가 등록되었습니다.');
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '문의 등록 실패');
    } finally { setBusy(false); }
  };

  // STEP 5-CT-B · 소비자 UI 는 항상 동일한 5-group 을 노출 (진입점 무관, 기본 선택만 차등)
  const titleLabel = scope === 'order' ? '주문 문의하기' : scope === 'product' ? '상품 문의하기' : '판매자 문의하기';

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center" onClick={onClose} data-testid="inq-composer">
      <div className="w-full sm:max-w-md bg-white dark:bg-[#0A0A0A] rounded-t-2xl sm:rounded-2xl p-4 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <div className="inline-flex items-center gap-2">
            <MessageCircle size={16} className="text-[#FF3B30]" />
            <p className="font-black text-base">{titleLabel}</p>
          </div>
          <button onClick={onClose} data-testid="inq-composer-close"><X size={16} /></button>
        </div>

        {context?.title && (
          <p className="text-[11px] text-gray-500 border-b border-black/10 dark:border-white/10 pb-2">{context.title}</p>
        )}

        {!authChecked ? (
          <p className="text-xs text-gray-500 py-6 text-center">불러오는 중…</p>
        ) : !me ? (
          // Phase 13-C: consumer-facing inquiries require LIVEMOA login.
          <div className="text-center py-6 space-y-3" data-testid="inq-login-gate">
            <div className="mx-auto w-12 h-12 rounded-full bg-[#FEE500] inline-flex items-center justify-center">
              <LogIn size={20} className="text-black" />
            </div>
            <p className="text-sm font-bold">문의하려면 로그인이 필요합니다</p>
            <p className="text-[11px] text-gray-500">
              로그인 후 이 페이지로 자동으로 돌아옵니다.<br />
              작성 중이던 문의 정보는 다시 열어주셔야 합니다.
            </p>
            <button
              onClick={startKakao}
              data-testid="inq-kakao-login"
              className="w-full py-3 rounded-full bg-[#FEE500] hover:bg-[#F5D900] text-black text-sm font-black inline-flex items-center justify-center gap-2"
            >
              카카오로 시작하기
            </button>
          </div>
        ) : (
          <>
            {/* Read-only member card — no re-typing name/phone/email */}
            <div className="p-3 rounded-lg bg-black/[0.03] dark:bg-white/[0.05] text-[11px] space-y-1" data-testid="inq-composer-buyer">
              <p className="font-black text-[10px] text-gray-500">문의자</p>
              <p className="inline-flex items-center gap-1 font-bold"><UserIcon size={11} /> {me.name || me.kakao_nickname || me.email || me.user_id}</p>
              {me.phone && <p className="inline-flex items-center gap-1 text-gray-500"><Phone size={11} /> {me.phone}</p>}
              {me.email && <p className="inline-flex items-center gap-1 text-gray-500"><Mail size={11} /> {me.email}</p>}
              {!me.phone && (
                <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                  ⓘ 회원정보에 연락처가 없습니다.{' '}
                  <a
                    href={`/profile?return_url=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                    className="underline font-bold hover:text-amber-700 dark:hover:text-amber-300"
                    data-testid="inq-composer-add-phone"
                  >
                    연락처 등록하기
                  </a>
                </p>
              )}
            </div>

            <select value={groupKey} onChange={(e) => setGroupKey(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
              data-testid="inq-composer-category">
              {CONSUMER_GROUPS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>

            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="문의 제목"
              className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
              data-testid="inq-composer-title" />
            <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={5} placeholder="문의 내용"
              className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
              data-testid="inq-composer-content" />

            {/* STEP 5-CS-B · 이미지 첨부 (최대 5장) · 기존 /api/uploads 재사용 */}
            <div className="space-y-2" data-testid="inq-composer-attach">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-black uppercase tracking-widest text-gray-500 inline-flex items-center gap-1">
                  <Paperclip size={11}/> 사진 첨부
                  <span className="text-gray-400 font-normal ml-1">({attachments.length}/{MAX_ATTACHMENTS})</span>
                </p>
                <span className="text-[10px] text-gray-400">JPG · PNG · WebP · 최대 5MB</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                {attachments.map((a, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5" data-testid={`inq-composer-attach-thumb-${i}`}>
                    <SafeImg src={a.url} kind="broadcast" alt={`첨부 ${i+1}`} className="w-full h-full object-cover"/>
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      className="absolute top-0.5 right-0.5 p-0.5 bg-black/70 hover:bg-[#FF3B30] text-white rounded-full"
                      data-testid={`inq-composer-attach-remove-${i}`}
                      aria-label={`첨부 ${i+1} 삭제`}
                    >
                      <X size={10}/>
                    </button>
                  </div>
                ))}
                {attachments.length < MAX_ATTACHMENTS && (
                  <label
                    className={`relative aspect-square rounded-lg border-2 border-dashed cursor-pointer flex flex-col items-center justify-center text-gray-400 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors ${
                      uploadingIdx >= 0 ? 'border-[#FF3B30]/40 bg-[#FF3B30]/5' : 'border-black/15 dark:border-white/15'
                    }`}
                    data-testid="inq-composer-attach-add"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={IMG_ACCEPT}
                      multiple
                      className="hidden"
                      onChange={(e) => uploadFiles(e.target.files)}
                      data-testid="inq-composer-attach-input"
                    />
                    {uploadingIdx >= 0 ? (
                      <><Loader2 size={16} className="animate-spin"/><span className="text-[9px] mt-1">업로드…</span></>
                    ) : (
                      <><ImageIcon size={16}/><span className="text-[9px] mt-1 font-bold">+ 추가</span></>
                    )}
                  </label>
                )}
              </div>
            </div>

            {scope === 'product' && (
              <label className="text-[11px] text-gray-500 inline-flex items-center gap-1">
                <input type="checkbox" checked={visibility === 'public'} onChange={(e) => setVisibility(e.target.checked ? 'public' : 'private')} />
                상품 Q&A 에 공개 (선택 시 다른 소비자에게도 보임)
              </label>
            )}

            <div className="flex gap-2 pt-1">
              <button onClick={onClose} className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/10 text-sm font-bold">취소</button>
              <button onClick={submit} disabled={busy || uploadingIdx >= 0}
                className="flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-sm font-bold disabled:opacity-40"
                data-testid="inq-composer-submit">
                {busy ? '등록 중…' : '문의 등록'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
